<script setup lang="ts">
import { ref, computed, inject, Ref, onMounted, watch } from 'vue'
import { Button } from '@/components/ui/button'
// Tabs replaced with custom tab buttons for reliable switching
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'vue-sonner'
import MonacoEditor from '@/components/ui/MonacoEditor.vue'
import { useApplications, type ApplicationTemplate } from '@/services/Applications'
import { createFmCompletionProvider } from '@/services/fmCompletionProvider'
import { fmComponents, fmCategories, type FMComponentDef } from '@/services/fmComponentRegistry'
import { Member } from '@/types/Member'
import {
	Settings, Languages, Archive, FileText, Columns2, Mail,
	Code2, Save, Plus, Pencil, Copy, Trash2, Lock, Unlock,
	Loader2, FileCode, Search, ChevronRight, ArrowLeft, X,
	Braces, Component as ComponentIcon,
} from 'lucide-vue-next'

// ─── Props (from route) ───
const props = defineProps<{
	appId?: string
}>()

// ─── Injections ───
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

// ─── Services ───
const {
	applications, templates, loadingTemplates,
	getApplicationList, getApplicationTemplates,
	getTemplateContent, saveTemplateContent,
} = useApplications()

// ─── State ───
type SettingTab = 'page-templates' | 'wrapper-templates' | 'email-templates' | 'css-templates' | 'sub-templates'

const activeTab = ref<SettingTab>('page-templates')
const loadingApps = ref(true)
const selectedAppId = ref<number | null>(null)

// Template editing
const editingTemplate = ref<ApplicationTemplate | null>(null)
const editorContent = ref('')
const editorDirty = ref(false)
const savingTemplate = ref(false)
const loadingContent = ref(false)

// Component library sidebar
const showComponentLib = ref(false)
const componentSearch = ref('')

// ─── Computed ───
const selectedApp = computed(() =>
	applications.value.find(a => a.id === String(selectedAppId.value))
)

const templateTypeMap: Record<SettingTab, string> = {
	'page-templates': 'apptemplates',
	'wrapper-templates': 'wrappers',
	'email-templates': 'emails',
	'css-templates': 'css',
	'sub-templates': 'subtemplates',
}

const tabDefs = [
	{ value: 'page-templates' as SettingTab, label: 'Pages', icon: FileText },
	{ value: 'sub-templates' as SettingTab, label: 'Subs', icon: Braces },
	{ value: 'wrapper-templates' as SettingTab, label: 'Wraps', icon: Columns2 },
	{ value: 'email-templates' as SettingTab, label: 'Emails', icon: Mail },
	{ value: 'css-templates' as SettingTab, label: 'CSS', icon: Code2 },
]

const switchTab = (tab: SettingTab) => {
	if (activeTab.value === tab) return
	editingTemplate.value = null
	editorContent.value = ''
	activeTab.value = tab
	loadTemplates()
}

const editorLanguage = computed(() =>
	activeTab.value === 'css-templates' ? 'css' : 'html'
)

const filteredComponents = computed(() => {
	const q = componentSearch.value.toLowerCase()
	if (!q) return fmComponents
	return fmComponents.filter(c =>
		c.name.toLowerCase().includes(q) ||
		c.description.toLowerCase().includes(q) ||
		c.category.toLowerCase().includes(q)
	)
})

const groupedComponents = computed(() => {
	const groups: Record<string, FMComponentDef[]> = {}
	for (const comp of filteredComponents.value) {
		if (!groups[comp.category]) groups[comp.category] = []
		groups[comp.category].push(comp)
	}
	return groups
})

// ─── FMComponent completion provider for Monaco ───
// Create once (not computed) to avoid re-registration loops via deep watcher
const fmCompletionProviders = [createFmCompletionProvider()]

// ─── Methods ───
const loadApplications = async () => {
	if (!vhostId?.value) return
	loadingApps.value = true
	try {
		const apps = await getApplicationList(vhostId.value)
		if (apps.length > 0) {
			// Use appId from route prop if provided, otherwise default to first app
			if (props.appId) {
				const targetId = parseInt(props.appId)
				const found = apps.find(a => parseInt(a.id) === targetId)
				selectedAppId.value = found ? targetId : parseInt(apps[0].id)
			} else {
				selectedAppId.value = parseInt(apps[0].id)
			}
		}
	} catch (e) {
		toast.error('Failed to load applications')
	} finally {
		loadingApps.value = false
	}
}

const loadTemplates = async () => {
	if (!selectedAppId.value) return
	const type = templateTypeMap[activeTab.value]
	try {
		await getApplicationTemplates(selectedAppId.value, type)
	} catch (e) {
		toast.error('Failed to load templates')
	}
}

const openTemplate = async (tpl: ApplicationTemplate) => {
	if (!selectedAppId.value) return
	editingTemplate.value = tpl
	loadingContent.value = true
	editorDirty.value = false

	const type = templateTypeMap[activeTab.value]
	try {
		const content = await getTemplateContent(selectedAppId.value, type, tpl.name)
		editorContent.value = content?.content || content?.publicdata || ''
	} catch (e) {
		editorContent.value = ''
		toast.error('Failed to load template content')
	} finally {
		loadingContent.value = false
	}
}

const closeEditor = () => {
	if (editorDirty.value) {
		if (!confirm('You have unsaved changes. Close anyway?')) return
	}
	editingTemplate.value = null
	editorContent.value = ''
	editorDirty.value = false
}

const saveTemplate = async () => {
	if (!selectedAppId.value || !editingTemplate.value) return
	savingTemplate.value = true
	const type = templateTypeMap[activeTab.value]
	try {
		const success = await saveTemplateContent(
			selectedAppId.value,
			type,
			editingTemplate.value.name,
			editorContent.value
		)
		if (success) {
			editorDirty.value = false
			toast.success('Template saved')
		} else {
			toast.error('Failed to save template')
		}
	} catch (e) {
		toast.error('Error saving template')
	} finally {
		savingTemplate.value = false
	}
}

const onEditorChange = (val: string) => {
	editorContent.value = val
	editorDirty.value = true
}

const insertComponent = (comp: FMComponentDef) => {
	const snippet = comp.snippet || (comp.selfClosing
		? `<fm:${comp.name} />`
		: `<fm:${comp.name}>\n\t\n</fm:${comp.name}>`)

	// Append at cursor via Monaco or just append to content
	editorContent.value += '\n' + snippet
	editorDirty.value = true
	toast.success(`Inserted <fm:${comp.name}>`)
}

const formatDate = (dateStr: string): string => {
	if (!dateStr) return '—'
	try {
		const d = new Date(dateStr)
		return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
	} catch { return dateStr }
}

// ─── Lifecycle ───
onMounted(loadApplications)

watch(selectedAppId, () => {
	editingTemplate.value = null
	editorContent.value = ''
	loadTemplates()
})

// Tab switching is handled by switchTab() method directly
</script>

<template>
	<div class="h-full flex flex-col">
		<!-- App selector (if multiple apps) -->
		<div v-if="loadingApps" class="flex items-center justify-center py-12">
			<Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
		</div>

		<template v-else>
			<div class="flex-1 flex flex-col min-h-0">
				<!-- Tab bar -->
				<div class="border-b px-2 md:px-4 pt-2 flex flex-col md:flex-row md:items-center gap-1 md:gap-3 bg-background shrink-0">
					<div class="flex gap-0 overflow-x-auto flex-nowrap scrollbar-hide">
						<button
							v-for="tab in tabDefs"
							:key="tab.value"
							class="relative flex items-center gap-1.5 px-3 md:px-4 py-2.5 text-xs md:text-sm whitespace-nowrap transition-colors"
							:class="activeTab === tab.value
								? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary'
								: 'text-muted-foreground hover:text-foreground'"
							@click="switchTab(tab.value)">
							<component :is="tab.icon" class="h-4 w-4" />
							{{ tab.label }}
						</button>
					</div>

					<!-- App selector on the right -->
					<div class="md:ml-auto flex items-center gap-2 pb-2 md:pb-0" v-if="applications.length > 1">
						<Label class="text-xs text-muted-foreground">App:</Label>
						<select
							:value="selectedAppId"
							@change="selectedAppId = parseInt(($event.target as HTMLSelectElement).value)"
							class="text-sm border rounded px-2 py-1 bg-background flex-1">
							<option v-for="app in applications" :key="app.id" :value="app.id">{{ app.name }}</option>
						</select>
					</div>
				</div>

				<!-- Template list / Editor area -->
				<div class="flex-1 min-h-0 overflow-hidden">
					<!-- Editor mode -->
					<div v-if="editingTemplate" class="h-full flex flex-col overflow-hidden">
						<!-- Editor toolbar -->
						<div class="flex items-center justify-between px-2 md:px-4 py-2 border-b bg-muted/30 gap-2">
							<div class="flex items-center gap-2 md:gap-3 min-w-0">
								<button @click="closeEditor" class="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground shrink-0">
									<ArrowLeft class="h-4 w-4" />
								</button>
								<div class="min-w-0">
									<h3 class="text-sm font-medium flex items-center gap-2 truncate">
										<FileCode class="h-4 w-4 text-primary shrink-0" />
										<span class="truncate">{{ editingTemplate.name }}</span>
										<span v-if="editorDirty" class="text-[10px] bg-yellow-500/20 text-yellow-600 px-1.5 py-0.5 rounded font-normal shrink-0">Modified</span>
									</h3>
									<p class="text-xs text-muted-foreground hidden md:block">Last modified: {{ formatDate(editingTemplate.modified) }}</p>
								</div>
							</div>
							<div class="flex items-center gap-1.5 md:gap-2 shrink-0">
								<Button
									v-if="activeTab !== 'css-templates'"
									variant="outline"
									size="sm"
									@click="showComponentLib = !showComponentLib"
									class="hidden md:inline-flex"
									:class="{ 'bg-primary/10 border-primary/30': showComponentLib }">
									<ComponentIcon class="h-4 w-4 mr-1.5" />
									Components
								</Button>
								<Button
									v-if="activeTab !== 'css-templates'"
									variant="outline"
									size="icon"
									class="h-8 w-8 md:hidden"
									@click="showComponentLib = !showComponentLib"
									:class="{ 'bg-primary/10 border-primary/30': showComponentLib }">
									<ComponentIcon class="h-4 w-4" />
								</Button>
								<Button size="sm" @click="saveTemplate" :disabled="!editorDirty || savingTemplate" class="gap-1.5">
									<Loader2 v-if="savingTemplate" class="h-4 w-4 animate-spin" />
									<Save v-else class="h-4 w-4" />
									<span class="hidden md:inline">Save</span>
								</Button>
							</div>
						</div>

						<!-- Editor + component library -->
						<div class="flex-1 flex min-h-0 relative overflow-hidden">
							<!-- Component library sidebar -->
							<div v-if="showComponentLib && activeTab !== 'css-templates'"
								class="w-full md:w-72 absolute md:relative z-10 inset-0 md:inset-auto border-r flex flex-col bg-background overflow-hidden shrink-0">
								<div class="p-3 border-b">
									<div class="relative">
										<Search class="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
										<Input
											v-model="componentSearch"
											placeholder="Search components..."
											class="pl-8 h-8 text-sm" />
									</div>
								</div>
								<div class="flex-1 overflow-y-auto p-2 space-y-3">
									<div v-for="(comps, category) in groupedComponents" :key="category">
										<h4 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2 mb-1">{{ category }}</h4>
										<div class="space-y-0.5">
											<button
												v-for="comp in comps"
												:key="comp.name"
												class="w-full text-left px-2 py-1.5 rounded-md hover:bg-muted transition-colors group"
												@click="insertComponent(comp)"
												:title="comp.description">
												<div class="flex items-center gap-2">
													<code class="text-xs text-primary font-mono">{{ comp.name }}</code>
													<span v-if="comp.selfClosing" class="text-[9px] text-muted-foreground/50">/&gt;</span>
												</div>
												<p class="text-[11px] text-muted-foreground line-clamp-1 mt-0.5">{{ comp.description }}</p>
											</button>
										</div>
									</div>
								</div>
							</div>

							<!-- Monaco editor -->
							<div class="flex-1 min-h-0 min-w-0 overflow-hidden">
								<div v-if="loadingContent" class="flex items-center justify-center h-full min-h-[400px]">
									<Loader2 class="h-6 w-6 animate-spin text-muted-foreground" />
								</div>
								<MonacoEditor
									v-else
									:modelValue="editorContent"
									:language="editorLanguage"
									:completionProviders="activeTab !== 'css-templates' ? fmCompletionProviders : []"
									:options="{ minimap: { enabled: false }, fontSize: 14, tabSize: 2 }"
									style="height: 100%; min-height: 400px;"
									@change="onEditorChange" />
							</div>
						</div>
					</div>

					<!-- Template list mode -->
					<div v-else class="h-full overflow-y-auto">
						<!-- Loading state -->
						<div v-if="loadingTemplates" class="p-6 space-y-3">
							<Skeleton v-for="i in 6" :key="i" class="h-14 w-full rounded-lg" />
						</div>

						<!-- Empty state -->
						<div v-else-if="templates.length === 0" class="flex flex-col items-center justify-center py-20 gap-4">
							<div class="h-14 w-14 rounded-full bg-muted flex items-center justify-center">
								<FileText class="h-7 w-7 text-muted-foreground" />
							</div>
							<div class="text-center">
								<p class="text-base font-medium">No templates found</p>
								<p class="text-sm text-muted-foreground mt-1">
									{{ selectedApp ? `No ${activeTab.replace('-', ' ')} for ${selectedApp.name}` : 'Select an application to view templates' }}
								</p>
							</div>
						</div>

						<!-- Template list — table header -->
						<div v-else>
							<div class="hidden md:grid grid-cols-[1fr_1.2fr_140px_80px_40px] gap-3 px-5 py-2 border-b bg-muted/20 text-xs font-medium text-muted-foreground uppercase tracking-wider">
								<span>Name</span>
								<span>URL</span>
								<span>Modified</span>
								<span>By</span>
								<span></span>
							</div>
							<div class="divide-y">
								<button
									v-for="tpl in templates"
									:key="tpl.id"
									class="w-full flex md:grid md:grid-cols-[1fr_1.2fr_140px_80px_40px] items-center gap-3 md:gap-3 px-5 py-3 text-left hover:bg-muted/40 transition-colors group"
									@click="openTemplate(tpl)">

									<!-- Name + icon -->
									<div class="flex items-center gap-3 min-w-0">
										<div class="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
											:class="activeTab === 'css-templates' ? 'bg-purple-500/10 text-purple-500' :
												activeTab === 'email-templates' ? 'bg-blue-500/10 text-blue-500' :
												activeTab === 'wrapper-templates' ? 'bg-orange-500/10 text-orange-500' :
												activeTab === 'sub-templates' ? 'bg-emerald-500/10 text-emerald-500' :
												'bg-primary/10 text-primary'">
											<Code2 v-if="activeTab === 'css-templates'" class="h-3.5 w-3.5" />
											<Mail v-else-if="activeTab === 'email-templates'" class="h-3.5 w-3.5" />
											<Columns2 v-else-if="activeTab === 'wrapper-templates'" class="h-3.5 w-3.5" />
											<Braces v-else-if="activeTab === 'sub-templates'" class="h-3.5 w-3.5" />
											<FileText v-else class="h-3.5 w-3.5" />
										</div>
										<div class="min-w-0">
											<p class="text-sm font-medium truncate">{{ tpl.name }}</p>
											<!-- Mobile-only: show URL + date inline -->
											<p class="text-xs text-muted-foreground mt-0.5 truncate md:hidden">
												{{ tpl.url || '—' }}
												<span class="mx-1">·</span>
												{{ formatDate(tpl.modified) }}
											</p>
										</div>
									</div>

									<!-- URL (desktop) -->
									<p class="hidden md:block text-xs text-muted-foreground truncate" :title="tpl.url">
										{{ tpl.url || '—' }}
									</p>

									<!-- Modified date (desktop) -->
									<p class="hidden md:block text-xs text-muted-foreground tabular-nums">
										{{ formatDate(tpl.modified) }}
									</p>

									<!-- Modified by (desktop) -->
									<p class="hidden md:block text-xs text-muted-foreground tabular-nums">
										{{ tpl.modifiedby || '—' }}
									</p>

									<!-- Status icons + chevron -->
									<div class="flex items-center gap-1.5 justify-end ml-auto md:ml-0">
										<span v-if="tpl.isLocked" class="text-amber-500" title="Locked">
											<Lock class="h-3.5 w-3.5" />
										</span>
										<span v-if="tpl.locale" class="text-[10px] text-muted-foreground/60 hidden md:inline" :title="'Locale: ' + tpl.locale">
											{{ tpl.locale }}
										</span>
										<ChevronRight class="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
									</div>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>
